import { Component } from '@theme/component';

/**
 * Filters a collection grid in place by product type and scent.
 *
 * This replaces the filtering that sections/shavingart__main-collection.liquid used to
 * provide. That section looped the global `collections` object, so every collection URL
 * shipped the whole catalogue (~7MB) and filtered client side by hiding entire collection
 * groups. This filters only the products on the current page, so the server keeps sending
 * one collection and pagination stays correct.
 *
 * Both controls are single-select and combine: picking a type and a scent shows the
 * intersection. Values are stamped onto each <li> by the section, so no product data is
 * duplicated into JS.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [count]
 * @extends {Component<Refs>}
 */
class ShavingartCollectionGridFilters extends Component {
  // The grid is a sibling, not a descendant: the custom element wraps only the filter
  // toolbar so the controls can sit above the products. A ref would have to be inside
  // the component to resolve, so the list is queried from the section instead.
  requiredRefs = [];

  /** @type {HTMLElement | null} */
  #list = null;

  /** @type {Record<string, string>} */
  #active = { type: '', scent: '' };

  connectedCallback() {
    super.connectedCallback();
    this.#list = this.closest('.shavingart__collection-grid')?.querySelector(
      '.shavingart__collection-grid__list'
    ) ?? null;
    if (this.#list) this.#apply();
  }

  /** @param {MouseEvent} event */
  selectFilter(event) {
    const chip = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-value]');
    if (!chip) return;

    const group = chip.closest('[data-filter]');
    const name = group instanceof HTMLElement ? group.dataset.filter : undefined;
    if (!name || !group) return;

    for (const option of group.querySelectorAll('[data-value]')) {
      const selected = option === chip;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-checked', String(selected));
      option.setAttribute('tabindex', selected ? '0' : '-1');
    }

    this.#active[name] = chip.dataset.value ?? '';
    this.#apply();
  }

  #apply() {
    const { type, scent } = this.#active;
    let visible = 0;

    for (const item of this.#items) {
      // `data-scents` is a pipe-delimited list because a product can carry several
      // scents across its variants; matching on the delimited token avoids "Sandalwood"
      // also matching a hypothetical "Sandalwood & Cedar".
      const scents = `|${item.dataset.scents ?? ''}`;
      const matches =
        (!type || item.dataset.type === type) &&
        (!scent || scents.includes(`|${scent}|`));

      item.hidden = !matches;
      if (matches) visible += 1;
    }

    const { count } = this.refs;
    const noMatch = this.closest('.shavingart__collection-grid')?.querySelector(
      '.shavingart__collection-grid__no-match'
    );
    if (count) {
      count.textContent = visible === this.#items.length
        ? ''
        : `${visible} of ${this.#items.length}`;
    }
    if (noMatch) noMatch.hidden = visible > 0;
  }

  get #items() {
    return /** @type {HTMLElement[]} */ (this.#list ? [...this.#list.children] : []);
  }
}

if (!customElements.get('shavingart-collection-grid-filters')) {
  customElements.define('shavingart-collection-grid-filters', ShavingartCollectionGridFilters);
}
